import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import type { RequestVoidRequest } from "../services/invoicingApi";

const REFUND_METHODS = ["Cash", "MPESA", "BankTransfer", "Cheque"];

const formatMoney = (value: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 2,
  }).format(value);

interface RequestVoidDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceNumber?: string;
  paidAmount?: number;
  isSubmitting: boolean;
  onSubmit: (payload: Omit<RequestVoidRequest, "invoiceId">) => void;
}

export const RequestVoidDialog = ({
  open,
  onOpenChange,
  invoiceNumber,
  paidAmount = 0,
  isSubmitting,
  onSubmit,
}: RequestVoidDialogProps) => {
  const [reason, setReason] = useState("");
  const [initiateRefund, setInitiateRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundPaymentMethod, setRefundPaymentMethod] = useState("");
  const [refundPaymentReference, setRefundPaymentReference] = useState("");
  const [refundNotes, setRefundNotes] = useState("");

  const hasPaidAmount = paidAmount > 0;

  useEffect(() => {
    if (!open) {
      setReason("");
      setInitiateRefund(false);
      setRefundAmount("");
      setRefundPaymentMethod("");
      setRefundPaymentReference("");
      setRefundNotes("");
    }
  }, [open]);

  const canSubmit =
    reason.trim().length >= 5 &&
    (!initiateRefund ||
      (refundPaymentMethod &&
        Number(refundAmount) > 0 &&
        Number(refundAmount) <= paidAmount));

  const handleSubmit = () => {
    const payload: Omit<RequestVoidRequest, "invoiceId"> = {
      reason: reason.trim(),
      ...(initiateRefund && {
        initiateRefund: true,
        refundAmount: Number(refundAmount),
        refundPaymentMethod,
        refundPaymentReference: refundPaymentReference || undefined,
        refundNotes: refundNotes || undefined,
      }),
    };
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Request Invoice Void</DialogTitle>
          <DialogDescription>
            Submit a void request for{" "}
            <span className="font-medium">{invoiceNumber ?? "this invoice"}</span>. A
            manager will review and approve or reject it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Field>
            <FieldLabel htmlFor="reason">Reason for void request</FieldLabel>
            <Textarea
              id="reason"
              placeholder="Describe why this invoice should be voided (min 5 characters)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </Field>

          {hasPaidAmount && (
            <div className="rounded-lg border bg-amber-50 border-amber-200 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="initiateRefund"
                  checked={initiateRefund}
                  onCheckedChange={(checked) =>
                    setInitiateRefund(checked === true)
                  }
                />
                <Label htmlFor="initiateRefund" className="text-sm font-medium cursor-pointer">
                  Include refund request ({formatMoney(paidAmount)} paid)
                </Label>
              </div>

              {initiateRefund && (
                <div className="space-y-3 pt-1">
                  <Field>
                    <FieldLabel htmlFor="refundAmount">
                      Refund amount (max {formatMoney(paidAmount)})
                    </FieldLabel>
                    <Input
                      id="refundAmount"
                      type="number"
                      min={0.01}
                      max={paidAmount}
                      step={0.01}
                      placeholder={paidAmount.toString()}
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="refundMethod">Refund method</FieldLabel>
                    <Select
                      value={refundPaymentMethod}
                      onValueChange={setRefundPaymentMethod}
                    >
                      <SelectTrigger id="refundMethod">
                        <SelectValue placeholder="Select method" />
                      </SelectTrigger>
                      <SelectContent>
                        {REFUND_METHODS.map((m) => (
                          <SelectItem key={m} value={m}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="refundRef">
                      Payment reference{" "}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </FieldLabel>
                    <Input
                      id="refundRef"
                      placeholder="e.g. MPESA transaction ID"
                      value={refundPaymentReference}
                      onChange={(e) => setRefundPaymentReference(e.target.value)}
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="refundNotes">
                      Refund notes{" "}
                      <span className="text-muted-foreground font-normal">(optional)</span>
                    </FieldLabel>
                    <Textarea
                      id="refundNotes"
                      placeholder="Any additional notes for the refund"
                      value={refundNotes}
                      onChange={(e) => setRefundNotes(e.target.value)}
                      rows={2}
                    />
                  </Field>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Submitting…" : "Submit void request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
